'use client'

import { useState, type FormEvent } from 'react'
import { authClient } from '../../../lib/auth-client'
import { AuthShell, authStyles as s } from './AuthShell'

/**
 * The OTP email-verification step, shared by sign-up (right after account
 * creation) and sign-in (when an unverified account is blocked). Verifies
 * the 6-digit code, then ensures a session exists before handing control
 * back via `onVerified` — auto-sign-in-after-verification usually lands a
 * session, but we fall back to a password sign-in when a password is on
 * hand (the sign-up path). Callers own the redirect.
 */
export function VerifyEmailStep({
  email,
  password,
  onVerified,
  onBack,
  backLabel = 'Go back',
}: {
  email: string
  /** Present on the sign-up path; enables the post-verify sign-in fallback. */
  password?: string
  onVerified: () => void
  onBack: () => void
  backLabel?: string
}) {
  const [otp, setOtp] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleVerify(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    setNotice(null)
    const result = await authClient.emailOtp.verifyEmail({ email, otp })
    if (result.error) {
      setSubmitting(false)
      setError(result.error.message ?? 'That code is invalid or has expired.')
      return
    }
    const session = await authClient.getSession()
    if (!session.data && password) {
      await authClient.signIn.email({ email, password })
    }
    setSubmitting(false)
    onVerified()
  }

  async function handleResend() {
    setResending(true)
    setError(null)
    setNotice(null)
    const result = await authClient.emailOtp.sendVerificationOtp({
      email,
      type: 'email-verification',
    })
    setResending(false)
    if (result.error) {
      setError(result.error.message ?? 'Could not resend the code.')
      return
    }
    setNotice('A fresh code is on its way.')
  }

  return (
    <AuthShell
      title="Verify your email"
      subtitle={
        <>
          We sent a 6-digit code to <strong>{email}</strong>. Enter it below to finish signing in.
        </>
      }
      footer={
        <p className={`${s.foot} ${s.footTight}`}>
          Wrong email?{' '}
          <button type="button" className={s.linkButton} onClick={onBack}>
            {backLabel}
          </button>
        </p>
      }
    >
      <form onSubmit={handleVerify} className={s.form}>
        <div className={s.field}>
          <label className={s.label} htmlFor="verify-otp">
            Verification code
          </label>
          <input
            id="verify-otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            maxLength={6}
            placeholder="••••••"
            className={s.otpInput}
            autoFocus
          />
          <div className={s.resendRow}>
            <span>Didn&apos;t get it? Check spam.</span>
            <button
              type="button"
              className={s.linkButton}
              onClick={handleResend}
              disabled={resending}
            >
              {resending ? 'Sending…' : 'Resend code'}
            </button>
          </div>
        </div>
        {notice ? <span className={`${s.hint} ${s.hintOk}`}>{notice}</span> : null}
        {error ? <p className={s.error}>{error}</p> : null}
        <button type="submit" disabled={submitting || otp.length < 6} className={s.button}>
          {submitting ? 'Verifying…' : 'Verify & continue'}
        </button>
      </form>
    </AuthShell>
  )
}
